// Transcript upload form (transcripts.md): trimmed container/View pair hosted
// by TranscriptUploadDrawer. Mode toggle (Text/WAV), a 2-up field grid (regrids
// inside the 640px drawer), the utterance editor, and the staged status line.
// Draft/parse/validation rules live in lib/utteranceDraft; the create →
// presigned-PUT → complete pipeline lives in useTranscriptUpload.

import type { SyntheticEvent } from "react";
import { useState } from "react";

import {
  createDraftRow,
  isUploadBusy,
  parseUtteranceJson,
  serializeDraftRows,
  uploadErrorStatus,
  uploadStatusMessage,
  validateSubmission,
  type TextUtteranceInputMode,
  type TranscriptUploadMode,
  type TranscriptUploadStatus,
  type TranscriptUploadSubmit,
  type TranscriptUtteranceDraft,
} from "../../lib/utteranceDraft";
import {
  TRANSCRIPT_SUBMIT_GUIDE_URL,
  transcriptApiLlmPrompt,
} from "../../lib/transcriptApiDocs";
import {
  Button,
  Copyable,
  Dropzone,
  Grid,
  Inline,
  InlineError,
  Link,
  Progress,
  Spinner,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import { TextUtteranceEditor } from "./TextUtteranceEditor";

export interface TranscriptUploadFormProps {
  onSuccess?: () => void;
  onSubmit: TranscriptUploadSubmit;
}

export interface TranscriptUploadFormViewProps {
  includeMarkerIds: string;
  mode: TranscriptUploadMode;
  onAddRow: () => void;
  onFileChange: (file: File | null) => void;
  onIncludeMarkerIdsChange: (value: string) => void;
  onModeChange: (mode: TranscriptUploadMode) => void;
  onOccurredAtChange: (value: string) => void;
  onRemoveRow: (id: string) => void;
  onRowChange: (
    id: string,
    patch: Partial<Omit<TranscriptUtteranceDraft, "id">>,
  ) => void;
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
  onSuppressMarkerIdsChange: (value: string) => void;
  onUtteranceInputModeChange: (mode: TextUtteranceInputMode) => void;
  onUtteranceJsonChange: (value: string) => void;
  onVersionTagChange: (value: string) => void;
  rows: TranscriptUtteranceDraft[];
  occurredAt: string;
  status: TranscriptUploadStatus;
  suppressMarkerIds: string;
  utteranceInputMode: TextUtteranceInputMode;
  utteranceJsonError: string | null;
  utteranceJsonValue: string;
  versionTag: string;
}

export function TranscriptUploadForm({
  onSubmit,
  onSuccess,
}: TranscriptUploadFormProps) {
  const [mode, setMode] = useState<TranscriptUploadMode>("text");
  const [versionTag, setVersionTag] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [includeMarkerIds, setIncludeMarkerIds] = useState("");
  const [suppressMarkerIds, setSuppressMarkerIds] = useState("");
  const [rows, setRows] = useState<TranscriptUtteranceDraft[]>(() => [
    createDraftRow(),
  ]);
  const [utteranceInputMode, setUtteranceInputMode] =
    useState<TextUtteranceInputMode>("table");
  const [utteranceJsonValue, setUtteranceJsonValue] = useState("");
  const [utteranceJsonError, setUtteranceJsonError] = useState<string | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<TranscriptUploadStatus>({
    stage: "idle",
  });

  function handleRowChange(
    id: string,
    patch: Partial<Omit<TranscriptUtteranceDraft, "id">>,
  ): void {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  function handleRemoveRow(id: string): void {
    setRows((currentRows) => currentRows.filter((row) => row.id !== id));
  }

  function handleUtteranceInputModeChange(
    nextMode: TextUtteranceInputMode,
  ): void {
    if (nextMode === utteranceInputMode) {
      return;
    }

    if (nextMode === "json") {
      const nextJsonValue = serializeDraftRows(rows);
      const result = parseUtteranceJson(nextJsonValue);
      setUtteranceJsonValue(nextJsonValue);
      setUtteranceJsonError(result.error);
      if (result.rows) {
        setRows(result.rows);
      }
    }
    setUtteranceInputMode(nextMode);
  }

  function handleUtteranceJsonChange(value: string): void {
    setUtteranceJsonValue(value);
    const result = parseUtteranceJson(value);
    setUtteranceJsonError(result.error);
    if (result.rows) {
      setRows(result.rows);
    }
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      mode === "text" &&
      utteranceInputMode === "json" &&
      utteranceJsonError
    ) {
      setStatus({ message: utteranceJsonError, stage: "validation_error" });
      return;
    }

    const submission = validateSubmission(
      mode,
      versionTag,
      occurredAt,
      rows,
      selectedFile,
      includeMarkerIds,
      suppressMarkerIds,
    );

    if (typeof submission === "string") {
      setStatus({ message: submission, stage: "validation_error" });
      return;
    }

    setStatus({ stage: "creating" });
    try {
      await onSubmit(submission, setStatus);
      setStatus({
        message: "Transcript submitted.",
        stage: "success",
      });
      onSuccess?.();
    } catch (error) {
      setStatus(uploadErrorStatus(error));
    }
  }

  return (
    <TranscriptUploadFormView
      includeMarkerIds={includeMarkerIds}
      mode={mode}
      occurredAt={occurredAt}
      onAddRow={() => {
        setRows((currentRows) => [...currentRows, createDraftRow()]);
      }}
      onFileChange={setSelectedFile}
      onIncludeMarkerIdsChange={setIncludeMarkerIds}
      onModeChange={setMode}
      onOccurredAtChange={setOccurredAt}
      onRemoveRow={handleRemoveRow}
      onRowChange={handleRowChange}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      onSuppressMarkerIdsChange={setSuppressMarkerIds}
      onUtteranceInputModeChange={handleUtteranceInputModeChange}
      onUtteranceJsonChange={handleUtteranceJsonChange}
      onVersionTagChange={setVersionTag}
      rows={rows}
      status={status}
      suppressMarkerIds={suppressMarkerIds}
      utteranceInputMode={utteranceInputMode}
      utteranceJsonError={utteranceJsonError}
      utteranceJsonValue={utteranceJsonValue}
      versionTag={versionTag}
    />
  );
}

export function TranscriptUploadFormView({
  includeMarkerIds,
  mode,
  occurredAt,
  onAddRow,
  onFileChange,
  onIncludeMarkerIdsChange,
  onModeChange,
  onOccurredAtChange,
  onRemoveRow,
  onRowChange,
  onSubmit,
  onSuppressMarkerIdsChange,
  onUtteranceInputModeChange,
  onUtteranceJsonChange,
  onVersionTagChange,
  rows,
  status,
  suppressMarkerIds,
  utteranceInputMode,
  utteranceJsonError,
  utteranceJsonValue,
  versionTag,
}: TranscriptUploadFormViewProps) {
  const busy = isUploadBusy(status);
  const message = uploadStatusMessage(status);
  const jsonSubmitDisabled =
    mode === "text" &&
    utteranceInputMode === "json" &&
    utteranceJsonError != null;

  return (
    <form onSubmit={onSubmit}>
      <Stack gap={4}>
        <Stack gap={2}>
          <Text variant="caps">Input</Text>
          <Inline gap={4}>
            <Button
              aria-pressed={mode === "text"}
              disabled={busy}
              size="sm"
              variant={mode === "text" ? "primary" : "secondary"}
              onClick={() => {
                onModeChange("text");
              }}
            >
              Text
            </Button>
            <Button
              aria-pressed={mode === "audio"}
              disabled={busy}
              size="sm"
              variant={mode === "audio" ? "primary" : "secondary"}
              onClick={() => {
                onModeChange("audio");
              }}
            >
              WAV
            </Button>
          </Inline>
        </Stack>

        <Grid columns={2} gap={4}>
          <TextInput
            autoComplete="off"
            disabled={busy}
            label="Version tag"
            value={versionTag}
            onChange={(event) => {
              onVersionTagChange(event.currentTarget.value);
            }}
          />
          <TextInput
            autoComplete="off"
            disabled={busy}
            label="Occurred at"
            type="datetime-local"
            value={occurredAt}
            onChange={(event) => {
              onOccurredAtChange(event.currentTarget.value);
            }}
          />
          <TextInput
            autoComplete="off"
            disabled={busy}
            label="Include marker IDs"
            value={includeMarkerIds}
            onChange={(event) => {
              onIncludeMarkerIdsChange(event.currentTarget.value);
            }}
          />
          <TextInput
            autoComplete="off"
            disabled={busy}
            label="Suppress marker IDs"
            value={suppressMarkerIds}
            onChange={(event) => {
              onSuppressMarkerIdsChange(event.currentTarget.value);
            }}
          />
        </Grid>

        {mode === "text" ? (
          <TextUtteranceEditor
            busy={busy}
            inputMode={utteranceInputMode}
            jsonError={utteranceJsonError}
            jsonValue={utteranceJsonValue}
            rows={rows}
            onAddRow={onAddRow}
            onInputModeChange={onUtteranceInputModeChange}
            onJsonChange={onUtteranceJsonChange}
            onRemoveRow={onRemoveRow}
            onRowChange={onRowChange}
          />
        ) : (
          <Dropzone
            accept=".wav,audio/wav"
            disabled={busy}
            id="transcript-upload-wav-file"
            label="WAV file"
            onFileChange={onFileChange}
          />
        )}

        {message ? (
          <UploadStatusMessage message={message} status={status} />
        ) : null}

        <Inline>
          <Button disabled={busy || jsonSubmitDisabled} type="submit">
            Submit
          </Button>
        </Inline>

        <Stack gap={2}>
          <Text variant="caps">Submit via API</Text>
          <Link
            href={TRANSCRIPT_SUBMIT_GUIDE_URL}
            rel="noopener"
            target="_blank"
          >
            How to submit a transcript through the API
          </Link>
          <Copyable label="prompt for LLM" value={transcriptApiLlmPrompt()}>
            <Text variant="muted">
              Integration prompt for your LLM tooling.
            </Text>
          </Copyable>
        </Stack>
      </Stack>
    </form>
  );
}

function UploadStatusMessage({
  message,
  status,
}: {
  message: string;
  status: TranscriptUploadStatus;
}) {
  if (
    status.stage === "validation_error" ||
    status.stage === "api_error" ||
    status.stage === "upload_error"
  ) {
    return <InlineError>{message}</InlineError>;
  }

  if (isUploadBusy(status)) {
    return (
      <Stack gap={2}>
        <Inline gap={2}>
          <Spinner label={message} />
          <Text aria-live="polite" variant="muted">
            {message}
          </Text>
        </Inline>
        {status.stage === "uploading" && status.uploadPercent != null ? (
          <Progress label="Upload progress" value={status.uploadPercent} />
        ) : null}
      </Stack>
    );
  }

  return (
    <Text aria-live="polite" role="status">
      {message}
    </Text>
  );
}
